import type { Starter, TriggerTag } from '../Starter'

const blueprintThumbnail = '/assets/images/thumbnails/tb_database.svg'

const networkStarter = (
  id: string,
  name: string,
  description: string,
  options: { popular?: boolean; trigger?: TriggerTag } = {},
): Starter => ({
  id,
  name,
  description,
  thumbnailUrl: blueprintThumbnail,
  artifact: { kind: 'seed', seedId: id, diagramType: 'network' },
  job: ['model-cloud'],
  function: ['it-cloud'],
  growsInto: 'blueprint',
  collaborative: ['share-view'],
  requiredFlag: 'BLUEPRINT_ENABLED',
  curatedBy: 'creately',
  popular: options.popular,
  trigger: options.trigger,
})

export const cloudStarters: Starter[] = [
  networkStarter(
    'network-three-tier-web',
    'Three-tier web app',
    'Public entry · private app · data tier',
    { popular: true },
  ),
  networkStarter(
    'network-serverless-api',
    'Serverless API',
    'API · functions · events · durable data',
    { popular: true },
  ),
  networkStarter(
    'network-event-driven-platform',
    'Event-driven platform',
    'Event bus · queues · workers',
  ),
  networkStarter(
    'network-microservices-platform',
    'Microservices platform',
    'Ingress · services · shared data',
  ),
  networkStarter(
    'network-kubernetes-platform',
    'Kubernetes platform',
    'Cluster · workloads · secrets · telemetry',
    { popular: true },
  ),
  networkStarter(
    'network-data-platform',
    'Data and analytics platform',
    'Ingestion · lake · warehouse · search',
  ),
  networkStarter(
    'network-ai-rag-platform',
    'AI / RAG application',
    'API · docs · vector search · metadata',
  ),
  networkStarter(
    'network-vpc-baseline',
    'Public/private network baseline',
    'Zones · egress · route tables · controls',
    { popular: true },
  ),
  networkStarter(
    'network-hub-spoke',
    'Hub-and-spoke network',
    'Shared hub · spokes · inspection',
  ),
  networkStarter(
    'network-hybrid-connectivity',
    'Hybrid connectivity',
    'Branch edge · cloud private network',
  ),
  networkStarter(
    'network-multi-region-resilience',
    'Multi-region resilience',
    'Failover · replication · monitoring',
    { popular: true },
  ),
  networkStarter(
    'network-edge-delivery',
    'Edge delivery and DNS',
    'DNS · CDN · WAF · origins',
  ),
  networkStarter(
    'network-public-exposure-review',
    'Public exposure review',
    'Internet-facing services and findings',
    { trigger: 'audit-due' },
  ),
  networkStarter(
    'network-zero-trust-access',
    'Zero-trust access',
    'Identity · policy · private app access',
  ),
  networkStarter(
    'network-segmentation-zones',
    'Segmentation and trust zones',
    'Edge · DMZ · app · data zones',
  ),
  networkStarter(
    'network-encryption-secrets',
    'Encryption and secrets baseline',
    'Keys · certs · secrets · protected data',
  ),
  networkStarter(
    'network-observability-baseline',
    'Observability baseline',
    'Logs · metrics · alarms · audit trail',
  ),
  networkStarter(
    'network-cost-allocation',
    'Cost allocation view',
    'Tagged services and spend signals',
    { trigger: 'cost-review' },
  ),
  networkStarter(
    'network-incident-response',
    'Incident response topology',
    'Detection · queue · runbook · evidence',
    { trigger: 'incident' },
  ),
  networkStarter(
    'network-demo-security-review',
    'Security review demo',
    'Realistic findings and remediation signals',
    { popular: true, trigger: 'audit-due' },
  ),
  {
    // Plain flowchart artifact (trojan) — NOT blueprint-flagged; the
    // system face behind it is what the flag gates.
    id: 'incident-postmortem',
    name: 'Incident Postmortem',
    description: 'Timeline · five whys · actions',
    thumbnailUrl: '/assets/images/thumbnails/tb_flowchart.svg',
    artifact: { kind: 'seed', seedId: 'incident-postmortem', diagramType: 'flowchart' },
    job: ['map-process'],
    function: ['it-cloud'],
    growsInto: 'blueprint',
    trigger: 'incident',
    collaborative: ['share-view'],
    curatedBy: 'creately',
  },
]
